import React, { useCallback } from 'react';
import type { AnalysesPeriod, TimeFrame } from 'venn-utils';
import type { RangeType } from 'venn-ui-kit';
import type { FactorLensWithReturns } from 'venn-api';
import { getDateRange } from '../components/header/utils';

export const useUpdateExplicitDateRange = (
  setExplicitDateRange: React.Dispatch<React.SetStateAction<TimeFrame | undefined>>,
  factorLens: FactorLensWithReturns | undefined,
  actualTimeFrame: TimeFrame | undefined,
) => {
  const prevExplicitDateRangeParams = React.useRef<{
    selectedTimeFrame: TimeFrame;
    selectedPeriod: RangeType | undefined;
    analysesPeriod: AnalysesPeriod | undefined;
  }>();

  const updateExplicitDateRange = useCallback(
    (
      selectedTimeFrame: TimeFrame,
      selectedPeriod: RangeType | undefined,
      analysesPeriod: AnalysesPeriod | undefined,
    ) => {
      setExplicitDateRange((existing) => {
        const range = getDateRange(selectedTimeFrame, selectedPeriod, analysesPeriod, factorLens);

        // prevent infinite loop when the backend fails to calculate a date range, usually with option portfolios
        // where the date range is calculated, then a new analysis is triggered with the same params but no date range is returned
        if (
          prevExplicitDateRangeParams.current?.selectedTimeFrame === selectedTimeFrame &&
          selectedPeriod === prevExplicitDateRangeParams.current?.selectedPeriod &&
          analysesPeriod?.maxStartTime === prevExplicitDateRangeParams.current?.analysesPeriod?.maxStartTime &&
          analysesPeriod?.maxEndTime === prevExplicitDateRangeParams.current?.analysesPeriod?.maxEndTime &&
          existing !== undefined &&
          range === undefined
        ) {
          return existing;
        }

        prevExplicitDateRangeParams.current = {
          selectedTimeFrame,
          selectedPeriod,
          analysesPeriod,
        };

        if (
          (existing === range && existing === actualTimeFrame) ||
          (existing?.startTime === range?.from && existing?.endTime === range?.to)
        ) {
          return existing;
        }
        return range ? { startTime: range.from, endTime: range.to } : undefined;
      });
    },
    [factorLens, actualTimeFrame, setExplicitDateRange],
  );

  return { updateExplicitDateRange };
};
